import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { timesheets, type TimesheetItem, type AutofillDay } from '@/api/timesheets';
import { orgs } from '@/api/catalogs';
import { refs } from '@/api/refs';
import DatePopover from '@/components/DatePopover';
import { useConfirm } from '@/components/ConfirmProvider';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import { useFormValidation } from '@/hooks/useFormValidation';
import DocumentToolbar from '@/shared/components/DocumentToolbar';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  period_year: number;
  period_month: number;
  norm_days: number;
  comment: string;
  items: TimesheetItem[];
}

const EMPTY_FORM: FormState = {
  organization_id: '',
  document_number: '',
  document_date: new Date().toISOString().slice(0, 16),
  period_year: new Date().getFullYear(),
  period_month: new Date().getMonth() + 1,
  norm_days: 0,  // порожнє при створенні — бухгалтер вводить або «Заповнити за графіком»
  comment: '',
  items: [],
};

const newRow = (n: number, employeeId: string, norm: number): TimesheetItem => ({
  line_number: n,
  employee_id: employeeId,
  working_days: String(norm),
  vacation_days: '0',
  sick_days: '0',
  absence_days: '0',
});

const MONTHS = ['Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
  'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];

const fmt = (v: number) =>
  v.toLocaleString('uk-UA', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

function recalcWorking(
  item: TimesheetItem, norm: number,
  recalcHours: boolean, hoursPerDay: number | undefined,
): TimesheetItem {
  const v = Number(item.vacation_days || 0);
  const s = Number(item.sick_days || 0);
  const a = Number(item.absence_days || 0);
  const wd = Math.max(0, norm - v - s - a);
  const next: TimesheetItem = { ...item, working_days: wd.toFixed(2) };
  // Години слідують за відпрацьованими днями × годин-на-день З ГРАФІКА працівника
  // (для 4-денки 35год/тиждень це 8.75, а не 8). Окрім рядків де години введено
  // вручну (recalcHours=false) — їх не чіпаємо. Якщо графік невідомий — години
  // не чіпаємо взагалі (краще лишити як є, ніж проставити невірне ×8).
  if (recalcHours && hoursPerDay != null) {
    next.worked_hours = (wd * hoursPerDay).toFixed(2);
  }
  return next;
}

export default function TimesheetEditor() {
  const confirm = useConfirm();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  // UX β: фільтр org dropdown по payroll.manage permission
  const {
    availableOrgs,
    hasPermissionInAnyOrg,
    totalAccessibleOrgs,
    loading: permsLoading,
  } = usePermissionsByOrg('payroll.manage');
  const docQuery = useQuery({
    queryKey: ['timesheet', id],
    queryFn: () => timesheets.get(id!),
    enabled: !isNew,
  });

  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const numberPreview = useNumberPreview('doc.timesheets', form.organization_id, isNew);
  const [error, setError] = useState<string | null>(null);
  const v = useFormValidation();
  // Спосіб введення (як 1С): summary = зведено, daily = сітка по днях 1..31.
  const [viewMode, setViewMode] = useState<'summary' | 'daily'>('summary');
  // Посуточна сітка (frontend-state; у БД зберігаються лише підсумки working/hours).
  // Ключ — employee_id; значення — масив днів за графіком/календарем.
  const [daysByEmp, setDaysByEmp] = useState<Record<string, DayCell[]>>({});
  // Заповнення «Усіма»: лише штатні чи усі (включно з ЦПХ). За замовч. — штатні.
  const [staffOnly, setStaffOnly] = useState(true);
  // Рядки (за індексом), де години введено ВРУЧНУ — авто-перерахунок їх НЕ чіпає.
  // Чисто UI-стан, у БД не йде. recalcWorking перераховує години лише для
  // «авто»-рядків (не у цьому наборі).
  const [manualHours, setManualHours] = useState<Set<number>>(new Set());
  // Годин/день за графіком кожного працівника (employee_id → hours_per_day).
  // Наповнюється з autofill-відповіді. Використовується для перерахунку годин при
  // ручній правці днів відсутності: години = відпрацьовані дні × hours_per_day.
  const [hoursPerDayByEmp, setHoursPerDayByEmp] = useState<Record<string, number>>({});

  function validate(): boolean {
    return v.check([
      ['organization_id', form.organization_id, 'Оберіть організацію'],
      ['document_date', form.document_date, 'Вкажіть дату документа'],
      ['norm_days', form.norm_days, 'Вкажіть норму днів (1–31)'],
    ]);
  }
  function onSave() {
    if (!validate()) return;
    isNew ? create.mutate() : update.mutate();
  }
  function onSaveAndPost() {
    if (!validate()) return;
    if (form.items.length === 0) {
      setError('Додайте співробітників перед проведенням');
      return;
    }
    createAndPost.mutate();
  }
  // Для збереженого документа: «Провести» спершу зберігає правки, потім проводить.
  function onUpdateAndPost() {
    if (!validate()) return;
    if (form.items.length === 0) {
      setError('Додайте співробітників перед проведенням');
      return;
    }
    updateAndPost.mutate();
  }

  const empQ = useQuery({
    // Табель — не звільнені (active_only). staffOnly: лише STAFF чи усі (ЦПХ теж).
    queryKey: ['employees', 'timesheet', form.organization_id, staffOnly],
    queryFn: () => refs.employees(form.organization_id, {
      ...(staffOnly ? { employment_type: 'STAFF' } : {}),
      active_only: true,
    }),
    enabled: !!form.organization_id,
  });

  useEffect(() => {
    if (isNew) {
      const firstOrg = availableOrgs[0]?.id ?? '';
      setForm((f) => f.organization_id ? f : {
        ...f, organization_id: firstOrg,
      });
    } else if (docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        period_year: d.period_year,
        period_month: d.period_month,
        norm_days: d.norm_days,
        comment: d.comment ?? '',
        items: d.items.map((it) => ({
          line_number: it.line_number,
          employee_id: it.employee_id,
          working_days: String(it.working_days),
          vacation_days: String(it.vacation_days),
          sick_days: String(it.sick_days),
          absence_days: String(it.absence_days),
          worked_hours: it.worked_hours ?? null,
          days: it.days ?? null,
          comment: it.comment,
        })),
      });
      // Відновити посуточну сітку (режим «По днях») з days кожного рядка (0300).
      const grid: Record<string, DayCell[]> = {};
      for (const it of d.items) {
        if (it.days && it.days.length) {
          grid[it.employee_id] = it.days.map((dd) => ({
            day: dd.day, is_work: dd.code === 'W',
            hours: dd.hours, code: dd.code as DayCell['code'],
          }));
        }
      }
      setDaysByEmp(grid);
    }
  }, [isNew, docQuery.data, availableOrgs]);

  // Підвантажити годин/день за графіком для працівників ТЧ, яких ще нема у кеші —
  // щоб ручна правка днів відсутності одразу правильно перераховувала години
  // (за графіком людини, не ×8). Легкий запит; нічого не зберігає.
  useEffect(() => {
    const missing = Array.from(new Set(
      form.items
        .map((it) => it.employee_id)
        .filter((id) => id && !(id in hoursPerDayByEmp)),
    ));
    if (missing.length === 0 || !form.organization_id) return;
    let cancelled = false;
    timesheets.hoursPerDay({ organization_id: form.organization_id, employee_ids: missing })
      .then((res) => {
        if (cancelled) return;
        const add: Record<string, number> = {};
        for (const [emp, hpd] of Object.entries(res)) {
          if (hpd != null) add[emp] = hpd;
        }
        if (Object.keys(add).length) setHoursPerDayByEmp((prev) => ({ ...prev, ...add }));
      })
      .catch(() => { /* графік невідомий — години не чіпаємо, не помилка */ });
    return () => { cancelled = true; };
  }, [form.items, form.organization_id, hoursPerDayByEmp]);

  const isPosted = !isNew && (docQuery.data?.posted ?? false);

  const totalWorking = form.items.reduce((s, e) => s + Number(e.working_days || 0), 0);
  const totalVacation = form.items.reduce((s, e) => s + Number(e.vacation_days || 0), 0);
  const totalSick = form.items.reduce((s, e) => s + Number(e.sick_days || 0), 0);
  const totalAbsence = form.items.reduce((s, e) => s + Number(e.absence_days || 0), 0);

  // Зберегти посуточну сітку (режим «По днях») у days кожного рядка → у БД (0300).
  // Якщо сітки для працівника нема (режим «Зведено») — days=null (як було).
  function itemsWithDays(): TimesheetItem[] {
    return form.items.map((it) => {
      const grid = daysByEmp[it.employee_id];
      return {
        ...it,
        days: grid && grid.length
          ? grid.map((d) => ({ day: d.day, code: d.code ?? (d.is_work ? 'W' : 'OFF'), hours: d.hours }))
          : null,
      };
    });
  }

  const create = useMutation({
    mutationFn: () => timesheets.create({
      organization_id: form.organization_id,
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      period_year: form.period_year,
      period_month: form.period_month,
      norm_days: form.norm_days,
      comment: form.comment || null,
      items: itemsWithDays(),
    }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['timesheets'] });
      navigate(`/timesheets/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  // Зберегти та провести (як у наказах): create → post → перехід на документ.
  const createAndPost = useMutation({
    mutationFn: async () => {
      const doc = await timesheets.create({
        organization_id: form.organization_id,
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        period_year: form.period_year,
        period_month: form.period_month,
        norm_days: form.norm_days,
        comment: form.comment || null,
        items: itemsWithDays(),
      });
      await timesheets.post(doc.id);
      return doc;
    },
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['timesheets'] });
      navigate(`/timesheets/${doc.id}`);
    },
    onError: (e) => setError(extractError(e)),
  });
  const update = useMutation({
    mutationFn: () => timesheets.update(id!, {
      document_number: form.document_number,
      document_date: new Date(form.document_date).toISOString(),
      period_year: form.period_year,
      period_month: form.period_month,
      norm_days: form.norm_days,
      comment: form.comment || null,
      items: itemsWithDays(),
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['timesheet', id] }),
    onError: (e) => setError(extractError(e)),
  });
  // Зберегти та провести (для вже збереженого документа): спершу зберігаємо
  // правки форми у БД, потім проводимо — щоб «Провести» не ігнорувало
  // незбережені зміни (раніше треба було тиснути «Зберегти», тоді «Провести»).
  const updateAndPost = useMutation({
    mutationFn: async () => {
      await timesheets.update(id!, {
        document_number: form.document_number,
        document_date: new Date(form.document_date).toISOString(),
        period_year: form.period_year,
        period_month: form.period_month,
        norm_days: form.norm_days,
        comment: form.comment || null,
        items: itemsWithDays(),
      });
      await timesheets.post(id!);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['timesheet', id] });
      qc.invalidateQueries({ queryKey: ['timesheets'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const unpost = useMutation({
    mutationFn: () => timesheets.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['timesheet', id] });
      qc.invalidateQueries({ queryKey: ['timesheets'] });
    },
    onError: (e) => setError(extractError(e)),
  });
  const remove = useMutation({
    mutationFn: () => timesheets.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['timesheets'] });
      navigate('/timesheets');
    },
  });

  // Авто-заповнення за графіком+календарем (Порція C/D). Бере всіх штатних
  // (або вже доданих) → autofill endpoint → заповнює рядки + сітку + норму.
  const autofill = useMutation({
    mutationFn: () => {
      const empIds = form.items.length > 0
        ? form.items.filter((it) => it.employee_id).map((it) => it.employee_id)
        : (empQ.data ?? []).map((e) => e.id);
      if (empIds.length === 0) throw new Error('Немає співробітників для заповнення');
      return timesheets.autofill({
        organization_id: form.organization_id,
        period_year: form.period_year,
        period_month: form.period_month,
        employee_ids: empIds,
      });
    },
    onSuccess: (res) => {
      const grid: Record<string, DayCell[]> = {};
      const items: TimesheetItem[] = res.items.map((r, idx) => {
        grid[r.employee_id] = r.days.map((d: AutofillDay) => ({
          ...d, code: (d.is_work ? 'W' : 'OFF') as DayCell['code'],
        }));
        return {
          line_number: idx + 1,
          employee_id: r.employee_id,
          working_days: String(r.working_days),
          vacation_days: '0',
          sick_days: '0',
          absence_days: '0',
          worked_hours: r.worked_hours ? String(r.worked_hours) : null,
          comment: null,
        };
      });
      // Кешуємо годин/день за графіком кожного працівника — для перерахунку
      // годин при подальшій ручній правці днів відсутності у «Зведено».
      const hpd: Record<string, number> = {};
      for (const r of res.items) {
        if (r.hours_per_day != null) hpd[r.employee_id] = r.hours_per_day;
      }
      setHoursPerDayByEmp((prev) => ({ ...prev, ...hpd }));
      // Посуточну сітку зберігаємо ЛИШЕ якщо користувач реально працює у режимі
      // «По днях». У «Зведено» autofill заповнює тільки підсумки (норма/відпрацьовано),
      // а days лишаємо порожніми — інакше сітка мовчки збережеться у БД і потім
      // здаватиметься, що бухгалтер «заповнив по днях», хоча не заповнював.
      setDaysByEmp(viewMode === 'daily' ? grid : {});
      setForm((f) => ({ ...f, norm_days: res.norm_days, items }));
    },
    onError: (e) => setError(extractError(e)),
  });

  // Зібрати рядки з денних табелів за місяць (Порція E). Підсумовує дні/години.
  const buildDaily = useMutation({
    mutationFn: () => timesheets.buildFromDaily({
      organization_id: form.organization_id,
      period_year: form.period_year,
      period_month: form.period_month,
    }),
    onSuccess: (res) => {
      if (res.items.length === 0) {
        setError(`Денних табелів за період не знайдено (${res.daily_count}).`);
        return;
      }
      const grid: Record<string, DayCell[]> = {};
      const items: TimesheetItem[] = res.items.map((r, idx) => {
        // Заповнюємо й сітку (режим «по днях») — інакше комірки порожні.
        grid[r.employee_id] = r.days.map((d) => ({
          day: d.day, is_work: d.is_work, hours: d.hours,
          code: d.code as DayCell['code'],
        }));
        return {
          line_number: idx + 1,
          employee_id: r.employee_id,
          working_days: String(r.working_days),
          vacation_days: String(r.vacation_days),
          sick_days: String(r.sick_days),
          absence_days: String(r.absence_days),
          worked_hours: r.worked_hours ? String(r.worked_hours) : null,
          comment: null,
        };
      });
      setDaysByEmp(grid);
      setForm((f) => ({ ...f, items }));
      setError(null);
    },
    onError: (e) => setError(extractError(e)),
  });

  function setItem(i: number, patch: Partial<TimesheetItem>) {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) => {
        if (idx !== i) return e;
        const next = { ...e, ...patch };
        // Якщо змінюються причини відсутності — перераховуємо working_days від норми
        // (+ години = дні × годин/день з графіка, якщо години не введено вручну).
        const trigger = ['vacation_days', 'sick_days', 'absence_days'];
        if (trigger.some((k) => k in patch)) {
          return recalcWorking(next, f.norm_days, !manualHours.has(idx), hoursPerDayByEmp[next.employee_id]);
        }
        return next;
      }),
    }));
  }

  function recalcAllWorking() {
    setForm((f) => ({
      ...f,
      items: f.items.map((e, idx) =>
        recalcWorking(e, f.norm_days, !manualHours.has(idx), hoursPerDayByEmp[e.employee_id])),
    }));
  }

  function fillFromEmployees() {
    if (!empQ.data || empQ.data.length === 0) return;
    setForm((f) => ({
      ...f,
      items: empQ.data!.map((e, idx) => newRow(idx + 1, e.id, f.norm_days)),
    }));
  }

  // Режим «по днях»: введення у комірку. Парсимо текст у код:
  //  «В»/«в» → відпустка, «Х»/«х» → лікарняний, «П»/«п» → прогул,
  //  число → робочий день з цими годинами, порожньо → вихідний/неробочий.
  function setDayValue(empId: string, dayIdx: number, raw: string) {
    if (isPosted) return;
    const parsed = parseDayCell(raw);
    setDaysByEmp((prev) => {
      const days = prev[empId];
      if (!days) return prev;
      const updated = days.map((d, i) =>
        i === dayIdx ? { ...d, ...parsed } : d,
      );
      recalcRowFromGrid(empId, updated);
      return { ...prev, [empId]: updated };
    });
  }

  // Перерахунок підсумків рядка з сітки днів (робочі/В/Х/П).
  function recalcRowFromGrid(empId: string, days: DayCell[]) {
    const wd = days.filter((d) => d.code === 'W' || (d.is_work && !d.code)).length;
    const wh = days.reduce((s, d) =>
      s + ((d.code === 'W' || (d.is_work && !d.code)) ? d.hours : 0), 0);
    const vac = days.filter((d) => d.code === 'V').length;
    const sick = days.filter((d) => d.code === 'S').length;
    const absc = days.filter((d) => d.code === 'A').length;
    setForm((f) => ({
      ...f,
      items: f.items.map((it) => it.employee_id === empId
        ? {
            ...it, working_days: String(wd),
            worked_hours: wh ? String(wh) : null,
            vacation_days: String(vac), sick_days: String(sick),
            absence_days: String(absc),
          }
        : it),
    }));
  }

  function addRow() {
    setForm((f) => ({
      ...f,
      items: [...f.items, newRow(f.items.length + 1, '', f.norm_days)],
    }));
  }
  function removeRow(i: number) {
    setForm((f) => ({
      ...f,
      items: f.items.filter((_, idx) => idx !== i)
        .map((e, idx) => ({ ...e, line_number: idx + 1 })),
    }));
    // Зсунути індекси manualHours: прибрати i, індекси > i зменшити на 1.
    setManualHours((prev) => {
      const next = new Set<number>();
      for (const idx of prev) {
        if (idx < i) next.add(idx);
        else if (idx > i) next.add(idx - 1);
      }
      return next;
    });
  }

  // UX β: warning якщо новий документ а user не має payroll.manage у жодній org
  if (isNew && !permsLoading && !hasPermissionInAnyOrg) {
    return (
      <div className="card">
        <div style={{ padding: '1.5rem', textAlign: 'center' }}>
          <div style={{
            padding: '1rem', background: '#fef3c7', borderRadius: 6,
            border: '1px solid #fbbf24', color: '#92400e',
            maxWidth: 600, margin: '1rem auto',
          }}>
            ⚠ У вас немає прав створювати табель у жодній з ваших
            організацій ({totalAccessibleOrgs}). Зверніться до
            адміністратора tenant'а.
          </div>
          <button onClick={() => navigate('/timesheets')}>Назад</button>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew ? 'Новий табель' : `Табель № ${form.document_number}`}
          {isPosted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ проведено
            </span>
          )}
        </h2>
        <DocumentToolbar
          onBackToList={() => navigate('/timesheets')}
          onSave={onSave}
          saveDisabled={create.isPending || update.isPending || createAndPost.isPending}
          isPosted={isPosted}
          isNew={isNew}
          onPost={isNew ? onSaveAndPost : onUpdateAndPost}
          postPending={create.isPending || update.isPending || createAndPost.isPending || updateAndPost.isPending}
          postTitle="Зберігає поточні зміни та проводить документ"
          onUnpost={() => unpost.mutate()}
          unpostPending={unpost.isPending}
          onDelete={() => { confirm('Видалити табель?').then((ok) => { if (ok) remove.mutate(); }); }}
          deletePending={remove.isPending}
        />
      </div>

      {v.banner()}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <label>Організація <span style={{ color: 'var(--danger)' }}>*</span></label>
          <select value={form.organization_id}
                  onChange={(e) => { setForm({ ...form, organization_id: e.target.value }); v.clear('organization_id'); }}
                  disabled={!isNew}
                  style={v.inputStyle('organization_id')}>
            <option value="">—</option>
            {(isNew ? availableOrgs : orgList.data?.items ?? []).map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
          {isNew && availableOrgs.length < totalAccessibleOrgs && (
            <small style={{ color: '#64748b', fontSize: '0.75rem' }}>
              Показано {availableOrgs.length} з {totalAccessibleOrgs} ваших організацій
              (інші не дозволяють цю операцію)
            </small>
          )}
          {v.error('organization_id')}
        </div>
        <div className="field">
          <label>№ документа</label>
          <input value={form.document_number}
                 onChange={(e) => { setForm({ ...form, document_number: e.target.value }); v.clear('document_number'); }}
                 placeholder={isNew && numberPreview ? `буде: ${numberPreview}` : ''}
                 disabled={isPosted}
                 style={v.inputStyle('document_number')} />
          {v.error('document_number')}
        </div>
        <div className="field">
          <label>Дата <span style={{ color: 'var(--danger)' }}>*</span></label>
          <DatePopover withTime value={form.document_date}
                 onChange={(val) => { setForm({ ...form, document_date: val }); v.clear('document_date'); }}
                 disabled={isPosted}
                 style={v.inputStyle('document_date')} />
          {v.error('document_date')}
        </div>
        <div className="field">
          <label>Місяць</label>
          <select value={form.period_month}
                  onChange={(e) => setForm({ ...form, period_month: Number(e.target.value) })}
                  disabled={isPosted}>
            {MONTHS.map((m, i) => (
              <option key={i + 1} value={i + 1}>{m}</option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Рік</label>
          <input type="number" value={form.period_year}
                 onChange={(e) => setForm({ ...form, period_year: Number(e.target.value) })}
                 disabled={isPosted} />
        </div>
        <div className="field">
          <label>Норма днів <span style={{ color: 'var(--danger)' }}>*</span></label>
          <input type="number" min={1} max={31} value={form.norm_days || ''}
                 onChange={(e) => { setForm({ ...form, norm_days: Number(e.target.value) }); v.clear('norm_days'); }}
                 onBlur={recalcAllWorking}
                 disabled={isPosted}
                 style={v.inputStyle('norm_days')} />
          {v.error('norm_days')}
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h3 style={{ margin: 0 }}>Співробітники</h3>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Спосіб введення (як 1С) */}
          <div style={{ display: 'flex', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
            {(['summary', 'daily'] as const).map((m) => (
              <button key={m} onClick={() => setViewMode(m)}
                style={{
                  padding: '0.3rem 0.7rem', border: 'none', cursor: 'pointer',
                  fontSize: '0.8rem',
                  background: viewMode === m ? 'var(--primary)' : 'transparent',
                  color: viewMode === m ? '#fff' : 'var(--text-secondary)',
                }}>
                {m === 'summary' ? 'Зведено за період' : 'По днях періоду'}
              </button>
            ))}
          </div>
          {/* Хто потрапляє у «Усіма»: лише штатні чи усі (ЦПХ теж). */}
          {!isPosted && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}
                   title="ЦПУ — договори цивільно-правового характеру (не штатні)">
              <input type="checkbox" checked={!staffOnly}
                     onChange={(e) => setStaffOnly(!e.target.checked)} />
              Включно з ЦПУ
            </label>
          )}
          {!isPosted && (
            <button onClick={() => autofill.mutate()} disabled={autofill.isPending}
              title="Розрахувати робочі дні/години за графіком співробітника та виробничим календарем">
              Заповнити за графіком
            </button>
          )}
          {!isPosted && (
            <button onClick={() => buildDaily.mutate()} disabled={buildDaily.isPending}
              title="Зібрати підсумки з денних табелів за цей місяць та організацію">
              Зібрати з денних
            </button>
          )}
          {!isPosted && empQ.data && empQ.data.length > 0 && form.items.length === 0 && (
            <button onClick={fillFromEmployees}>
              Усіма ({empQ.data.length}{staffOnly ? ', штатні' : ', усі'})
            </button>
          )}
        </div>
      </div>
      {viewMode === 'daily' && (
        <p style={{ color: '#64748b', fontSize: '0.85rem', marginTop: '-0.3rem' }}>
          Сітка по днях: «р8» = робочий день (8 год), порожньо = вихідний/свято. Клік перемикає день. Підсумки рахуються автоматично. «Заповнити за графіком» проставить дні з графіка + календаря.
        </p>
      )}

      {viewMode === 'daily' && (
        <DailyGrid
          items={form.items}
          daysByEmp={daysByEmp}
          empOptions={empQ.data ?? []}
          isPosted={isPosted}
          daysInMonth={new Date(form.period_year, form.period_month, 0).getDate()}
          year={form.period_year}
          month={form.period_month}
          onSetDay={setDayValue}
          onRemove={removeRow}
        />
      )}

      {viewMode === 'summary' && (
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
        <thead>
          <tr style={{ background: '#f8fafc', textAlign: 'left', fontSize: '0.78rem' }}>
            <th style={{ padding: '0.2rem 0.4rem', width: 25 }}>#</th>
            <th style={{ padding: '0.2rem 0.4rem' }}>Співробітник</th>
            <th style={{ padding: '0.2rem 0.4rem', width: 90 }}>Відпустка</th>
            <th style={{ padding: '0.2rem 0.4rem', width: 90 }}>Лікарняний</th>
            <th style={{ padding: '0.2rem 0.4rem', width: 90 }}>Прогули</th>
            <th style={{ padding: '0.2rem 0.4rem', width: 100 }}>Відпрацьовано</th>
            <th style={{ padding: '0.2rem 0.4rem', width: 85 }}>Години</th>
            <th style={{ padding: '0.2rem 0.4rem' }}>Коментар</th>
            <th style={{ padding: '0.2rem 0.4rem', width: 35 }}></th>
          </tr>
        </thead>
        <tbody>
          {form.items.map((it, i) => (
            <tr key={i} style={{ borderTop: '1px solid #e2e8f0' }}>
              <td style={{ padding: '0.15rem 0.4rem' }}>{it.line_number}</td>
              <td style={{ padding: '0.15rem 0.4rem' }}>
                <select value={it.employee_id}
                        onChange={(e) => setItem(i, { employee_id: e.target.value })}
                        disabled={isPosted}
                        style={{ width: '100%' }}>
                  <option value="">—</option>
                  {empQ.data?.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.code ? `${e.code} — ` : ''}{e.name}
                    </option>
                  ))}
                </select>
              </td>
              <td style={{ padding: '0.15rem 0.4rem' }}>
                <input type="number" step="0.5" min={0} max={31} className="no-spin"
                       value={it.vacation_days}
                       onChange={(e) => setItem(i, { vacation_days: e.target.value })}
                       disabled={isPosted}
                       style={{ textAlign: 'right', width: '100%' }} />
              </td>
              <td style={{ padding: '0.15rem 0.4rem' }}>
                <input type="number" step="0.5" min={0} max={31} className="no-spin"
                       value={it.sick_days}
                       onChange={(e) => setItem(i, { sick_days: e.target.value })}
                       disabled={isPosted}
                       style={{ textAlign: 'right', width: '100%' }} />
              </td>
              <td style={{ padding: '0.15rem 0.4rem' }}>
                <input type="number" step="0.5" min={0} max={31} className="no-spin"
                       value={it.absence_days}
                       onChange={(e) => setItem(i, { absence_days: e.target.value })}
                       disabled={isPosted}
                       style={{ textAlign: 'right', width: '100%' }} />
              </td>
              <td style={{ padding: '0.15rem 0.4rem' }}>
                <input type="number" step="0.5" min={0} max={31} className="no-spin"
                       value={it.working_days}
                       onChange={(e) => setItem(i, { working_days: e.target.value })}
                       disabled={isPosted}
                       style={{ textAlign: 'right', width: '100%', fontWeight: 600, background: '#f1f5f9' }} />
              </td>
              <td style={{ padding: '0.15rem 0.4rem' }}>
                <input type="number" step="0.5" min={0} className="no-spin"
                       value={it.worked_hours ?? ''}
                       onChange={(e) => {
                         // Ручний ввід годин — позначаємо рядок, щоб авто-перерахунок
                         // (дні×годин-день) більше його не затирав.
                         setManualHours((prev) => new Set(prev).add(i));
                         setItem(i, { worked_hours: e.target.value || null });
                       }}
                       disabled={isPosted} placeholder="—"
                       title="Години (з графіка авто; можна ввести вручну для неповного дня)"
                       style={{ textAlign: 'right', width: '100%' }} />
              </td>
              <td style={{ padding: '0.15rem 0.4rem' }}>
                <input value={it.comment ?? ''}
                       onChange={(e) => setItem(i, { comment: e.target.value })}
                       disabled={isPosted}
                       style={{ width: '100%' }} />
              </td>
              <td style={{ padding: '0.15rem 0.4rem' }}>
                {!isPosted && (
                  <button onClick={() => removeRow(i)} className="danger"
                          style={{ padding: '0.2rem 0.45rem' }}>×</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ borderTop: '2px solid #cbd5e1', fontWeight: 600, fontSize: '0.78rem' }}>
            <td colSpan={2} style={{ padding: '0.25rem 0.4rem', textAlign: 'right' }}>Усього:</td>
            <td style={{ padding: '0.25rem 0.4rem', textAlign: 'right' }}>{fmt(totalVacation)}</td>
            <td style={{ padding: '0.25rem 0.4rem', textAlign: 'right' }}>{fmt(totalSick)}</td>
            <td style={{ padding: '0.25rem 0.4rem', textAlign: 'right' }}>{fmt(totalAbsence)}</td>
            <td style={{ padding: '0.25rem 0.4rem', textAlign: 'right' }}>{fmt(totalWorking)}</td>
            <td colSpan={2}></td>
          </tr>
        </tfoot>
      </table>
      )}
      {viewMode === 'summary' && !isPosted && (
        <button onClick={addRow} style={{ marginTop: '0.75rem' }}>+ Додати рядок</button>
      )}

      <div className="field" style={{ marginTop: '1rem' }}>
        <label>Коментар</label>
        <input value={form.comment}
               onChange={(e) => setForm({ ...form, comment: e.target.value })}
               disabled={isPosted} />
      </div>

      {error && <div className="error">{error}</div>}

      {!isPosted && (
        <p style={{ marginTop: '1rem', color: '#64748b', fontSize: '0.85rem' }}>
          Табель — інформаційний документ. Після проведення його можна обрати у нарахуванні ЗП:
          оклад буде поділено пропорційно (оклад × відпрацьовано / норма).
        </p>
      )}
    </div>
  );
}

function extractError(e: unknown): string {
  if (typeof e === 'object' && e && 'response' in e) {
    const r = (e as { response?: { data?: { detail?: string } } }).response;
    return r?.data?.detail ?? 'Error';
  }
  return String(e);
}

const WD_SHORT = ['пн', 'вт', 'ср', 'чт', 'пт', 'сб', 'нд'];

// Комірка дня сітки: код W=робота, V=відпустка, S=лікарняний, A=прогул, OFF=вихідний.
type DayCell = AutofillDay & { code?: 'W' | 'V' | 'S' | 'A' | 'OFF' };

// Парсинг введеного у комірку тексту → {code, is_work, hours}.
// «В/V» відпустка, «Х/X/H» лікарняний, «П/P» прогул, число → робота N год,
// порожньо → вихідний.
function parseDayCell(raw: string): { code: DayCell['code']; is_work: boolean; hours: number } {
  const t = raw.trim().toUpperCase();
  if (t === '') return { code: 'OFF', is_work: false, hours: 0 };
  if (t === 'В' || t === 'V') return { code: 'V', is_work: false, hours: 0 };
  if (t === 'Х' || t === 'X' || t === 'H' || t === 'Л' || t === 'L')
    return { code: 'S', is_work: false, hours: 0 };
  if (t === 'П' || t === 'P') return { code: 'A', is_work: false, hours: 0 };
  const num = Number(t.replace(',', '.'));
  if (!isNaN(num) && num > 0) return { code: 'W', is_work: true, hours: num };
  return { code: 'OFF', is_work: false, hours: 0 };
}

// Текст у комірці за станом дня.
function dayCellText(d?: DayCell): string {
  if (!d) return '';
  if (d.code === 'V') return 'В';
  if (d.code === 'S') return 'Л';
  if (d.code === 'A') return 'П';
  if (d.code === 'W' || (d.is_work && !d.code)) return String(d.hours || 8);
  return '';
}

interface DailyGridProps {
  items: TimesheetItem[];
  daysByEmp: Record<string, DayCell[]>;
  empOptions: { id: string; code?: string | null; name: string }[];
  isPosted: boolean;
  daysInMonth: number;
  year: number;
  month: number;
  onSetDay: (empId: string, dayIdx: number, raw: string) => void;
  onRemove: (lineIndex: number) => void;
}

// Колір комірки за кодом дня.
function cellStyle(d?: DayCell): { bg: string; color: string } {
  if (!d) return { bg: '#f1f5f9', color: '#cbd5e1' };
  if (d.code === 'V') return { bg: '#eff6ff', color: '#2563eb' };   // відпустка
  if (d.code === 'S') return { bg: '#fef2f2', color: '#dc2626' };   // лікарняний
  if (d.code === 'A') return { bg: '#fffbeb', color: '#d97706' };   // прогул
  if (d.code === 'W' || (d.is_work && !d.code)) return { bg: '#ecfdf5', color: '#15803d' };
  return { bg: '#f1f5f9', color: '#cbd5e1' };
}

/** Сітка по днях (як 1С «По дням периода»): рядок=співробітник, колонки 1..31.
 *  Комірка редагується: число=години роботи, В/Х/П=відпустка/лікарняний/прогул,
 *  порожньо=вихідний. Підсумки рахуються автоматично. */
function DailyGrid({
  items, daysByEmp, empOptions, isPosted, daysInMonth, year, month,
  onSetDay, onRemove,
}: DailyGridProps) {
  // Активна комірка (клік) → показуємо панель дій під сіткою.
  const [active, setActive] = useState<{ empId: string; idx: number } | null>(null);
  const [hoursInput, setHoursInput] = useState('');

  const empName = (id: string) => {
    const e = empOptions.find((o) => o.id === id);
    return e ? `${e.code ? e.code + ' — ' : ''}${e.name}` : '—';
  };
  const dayList = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const wd = (d: number) => (new Date(year, month - 1, d).getDay() + 6) % 7;

  if (items.length === 0) {
    return (
      <p style={{ color: '#64748b', fontSize: '0.85rem' }}>
        Немає рядків. Натисніть «Заповнити за графіком» або «Усіма».
      </p>
    );
  }

  function openCell(empId: string, idx: number) {
    if (isPosted) return;
    setActive({ empId, idx });
    setHoursInput('');
  }
  function apply(raw: string) {
    if (!active) return;
    onSetDay(active.empId, active.idx, raw);
  }

  return (
    <>
      <div style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 6 }}>
        <table style={{ borderCollapse: 'collapse', fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              {!isPosted && <th style={{ width: 24, position: 'sticky', left: 0, background: '#f8fafc' }} />}
              <th style={{ padding: '0.3rem 0.5rem', position: 'sticky', left: isPosted ? 0 : 24, background: '#f8fafc', textAlign: 'left', minWidth: 170 }}>
                Співробітник
              </th>
              {dayList.map((d) => {
                const we = wd(d) >= 5;
                return (
                  <th key={d} style={{
                    padding: '0.2rem', width: 30, textAlign: 'center',
                    color: we ? '#dc2626' : '#334155', fontWeight: we ? 700 : 500,
                  }}>
                    <div>{d}</div>
                    <div style={{ fontSize: '0.62rem', fontWeight: 400 }}>{WD_SHORT[wd(d)]}</div>
                  </th>
                );
              })}
              <th style={{ padding: '0.3rem 0.4rem', textAlign: 'center', minWidth: 40 }}>Дн</th>
              <th style={{ padding: '0.3rem 0.4rem', textAlign: 'center', minWidth: 48 }}>Год</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, rowIdx) => {
              const days = daysByEmp[it.employee_id] ?? [];
              return (
                <tr key={it.employee_id || it.line_number} style={{ borderTop: '1px solid #e2e8f0' }}>
                  {!isPosted && (
                    <td style={{ padding: '0.1rem', textAlign: 'center', position: 'sticky', left: 0, background: '#fff' }}>
                      <button onClick={() => onRemove(rowIdx)} className="danger"
                        title="Видалити рядок"
                        style={{ padding: '0.1rem 0.35rem', fontSize: '0.75rem' }}>×</button>
                    </td>
                  )}
                  <td style={{ padding: '0.3rem 0.5rem', position: 'sticky', left: isPosted ? 0 : 24, background: '#fff' }}>
                    {empName(it.employee_id)}
                  </td>
                  {dayList.map((d, idx) => {
                    const day = days[idx];
                    const st = cellStyle(day);
                    const isActive = active?.empId === it.employee_id && active?.idx === idx;
                    return (
                      <td key={d}
                        onClick={() => openCell(it.employee_id, idx)}
                        title={isPosted ? undefined : 'Клік — обрати дію для дня'}
                        style={{
                          padding: '0.25rem 0', textAlign: 'center',
                          background: st.bg, color: st.color, fontWeight: 600,
                          cursor: isPosted ? 'default' : 'pointer', userSelect: 'none',
                          outline: isActive ? '2px solid var(--primary)' : 'none',
                          outlineOffset: -2,
                        }}>
                        {dayCellText(day) || '·'}
                      </td>
                    );
                  })}
                  <td style={{ padding: '0.3rem 0.4rem', textAlign: 'center', fontWeight: 600 }}>
                    {it.working_days}
                  </td>
                  <td style={{ padding: '0.3rem 0.4rem', textAlign: 'center' }}>
                    {it.worked_hours ?? '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Панель дій для активної комірки (з'являється після кліку). */}
      {active && !isPosted && (() => {
        const it = items.find((x) => x.employee_id === active.empId);
        return (
          <div style={{
            marginTop: '0.5rem', padding: '0.6rem 0.8rem',
            border: '1px solid var(--primary)', borderRadius: 8,
            background: '#f8fafc', display: 'flex', gap: '0.5rem',
            alignItems: 'center', flexWrap: 'wrap',
          }}>
            <b style={{ fontSize: '0.85rem' }}>
              {it ? empName(it.employee_id) : ''} — {active.idx + 1}
              {' '}{MONTHS[month - 1]}:
            </b>
            <button onClick={() => apply('')} title="Очистити (вихідний)"
              style={{ padding: '0.3rem 0.6rem' }}>✕ Очистити</button>
            <button onClick={() => apply('П')} title="Прогул"
              style={{ padding: '0.3rem 0.6rem', color: '#d97706' }}>П — прогул</button>
            <button onClick={() => apply('Л')} title="Лікарняний"
              style={{ padding: '0.3rem 0.6rem', color: '#dc2626' }}>Л — лікарняний</button>
            <button onClick={() => apply('В')} title="Відпустка"
              style={{ padding: '0.3rem 0.6rem', color: '#2563eb' }}>В — відпустка</button>
            <span style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <input type="number" step="0.5" min={0} max={24}
                value={hoursInput} placeholder="години"
                onChange={(e) => setHoursInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && hoursInput) { apply(hoursInput); setHoursInput(''); }
                }}
                style={{ width: 80 }} />
              <button onClick={() => { if (hoursInput) { apply(hoursInput); setHoursInput(''); } }}
                className="primary" style={{ padding: '0.3rem 0.6rem' }}>↵</button>
            </span>
            <button onClick={() => setActive(null)}
              style={{ marginLeft: 'auto', padding: '0.3rem 0.6rem' }}>Закрити</button>
          </div>
        );
      })()}

      <p style={{ color: '#64748b', fontSize: '0.78rem', marginTop: '0.4rem' }}>
        Клік по дню → панель дій: <b>число</b> годин (Enter) · <b>В</b> відпустка ·
        <b> Л</b> лікарняний · <b>П</b> прогул · <b>✕</b> очистити (вихідний).
      </p>
    </>
  );
}
